import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { BayclubsAlertService } from '@bayclubs/alert';
import { ErrorTranslationService } from '@bayclubs/utils';

import { campsenum } from '../../../models/enums.generated';
import { camps } from '../../../models/models.generated';

import { CampDefinitionApiService } from '../../../services/api/camp-definition-api.service';
import { CampsEnumTranslationService } from '../../../services/camps-enum-translation.service';

@Component({
    selector: 'app-camp-create',
    host: { 'data-testid': 'camp-create-page' },
    templateUrl: './camp-create.component.html',
    styleUrls: ['./camp-create.component.scss'],
    standalone: false,
})
export class CampCreateComponent implements OnInit {
    public model: camps.CampDefinitionDetails;
    public form: UntypedFormGroup;
    private parentCategories: camps.CategoryModel[] = [];
    private subcategories: camps.CategoryModel[] = [];

    public campTypes: campsenum.CampType[] = [];
    private disabledCampTypes = [campsenum.CampType.School, campsenum.CampType.Childcare];

    public currentParentCategories: WritableSignal<camps.CategoryModel[]> = signal<camps.CategoryModel[]>([]);
    public currentSubcategories: WritableSignal<camps.CategoryModel[]> = signal<camps.CategoryModel[]>([]);

    constructor(
        private api: CampDefinitionApiService,
        private formBuilder: UntypedFormBuilder,
        private alert: BayclubsAlertService,
        private router: Router,
        private translate: TranslateService,
        private errorTranslate: ErrorTranslationService,
        private campsEnumTranslation: CampsEnumTranslationService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.api.getCreationContext().subscribe((x) => {
            const primaryCategories = x.value.categories.filter((x) => !x.parentId);
            this.parentCategories.push(...primaryCategories);
            const subcategories = x.value.categories.filter((x) => x.parentId);
            this.subcategories.push(...subcategories);
            this.changeType(campsenum.CampType.Standard.toString());
         
            this.campTypes = x.value.campTypes.filter((type) => !this.disabledCampTypes.includes(type));
        });
    }

    public get categoryForm(): UntypedFormGroup {
        return this.form.controls['category'] as UntypedFormGroup;
    }

    initForm() {
        this.form = this.formBuilder.group({
            name: ['', [Validators.required]],
            category: this.formBuilder.group({
                id: [null, []],
                name: [null, []],
                parentId: [null, [Validators.required]],
                parentName: [null, [Validators.required]],
            }),
            campType: [campsenum.CampType.Standard, [Validators.required]],
        });
    }

    public create(): void {
        if (this.form.invalid) return;

        const request = <camps.CampCreateRequest>{
            name: this.form.value.name,
            category: this.form.controls['category'].value.parentName,
            subcategory: this.form.controls['category'].value.name,
            campType: this.form.value.campType,
        };

        this.api.create(request).subscribe((result) => {
            if (result.isSuccess) {
                this.router.navigate(['camps/' + result.value.id + '/details']);
                this.alert.showInfo(this.translate.instant('CampCreate.SuccessMessage'));
            } else {
                this.errorTranslate.translateValidationNotifications(result.validationNotification);
                this.alert.showError(result.validationNotification.errors[0].message);
            }
        });
    }

    public changeType(value: string): void {
        console.log('categories', this.currentParentCategories());
        const filteredCategories = this.parentCategories.filter((category) => {
            const result = category?.campType?.toString() === value;
            return result;
        });
        this.currentParentCategories.set(filteredCategories);

        const filteredSubcategories = this.subcategories.filter((subCat) => {
            const shouldBeIncluded = filteredCategories.some((parentCat) => parentCat.id === subCat.parentId);
            return shouldBeIncluded;
        });

        this.currentSubcategories.set(filteredSubcategories);
        this.form.controls['campType'].setValue(value);
        this.form.controls['category'].reset();
    }

    public translateType(campType: campsenum.CampType) {
        return this.campsEnumTranslation.campType(campType);
    }
}
